/**
 * VM Pool hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vmPoolApi } from '../services/vmApi';
import { VMPool, VMPoolCreate, VMPoolUpdate, VMPoolScale } from '../types/vmPool';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface UseVMPoolsParams {
  projectId?: string;
  page?: number;
  size?: number;
  enabled?: boolean;
}

export const useVMPools = (params: UseVMPoolsParams = {}) => {
  const { projectId, page = 1, size = 20, enabled = true } = params;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['vm-pools', projectId, page, size],
    queryFn: () => vmPoolApi.listPools({ project_id: projectId, page, size }),
    enabled: enabled,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    placeholderData: (previousData) => previousData,
  });

  const pools: VMPool[] = data?.pools || [];

  const createMutation = useMutation({
    mutationFn: (poolData: VMPoolCreate) => vmPoolApi.createPool(poolData, projectId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vm-pools'], refetchType: 'active' });
      toast.success('VM Pool creation initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to create VM Pool';
      toast.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ poolId, data }: { poolId: string; data: VMPoolUpdate }) =>
      vmPoolApi.updatePool(poolId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vm-pools'], refetchType: 'active' });
      toast.success('VM Pool updated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to update VM Pool';
      toast.error(errorMessage);
    },
  });

  const scaleMutation = useMutation({
    mutationFn: ({ poolId, replicas }: { poolId: string; replicas: number }) =>
      vmPoolApi.scalePool(poolId, replicas),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vm-pools'], refetchType: 'active' });
      toast.success('VM Pool scaling initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to scale VM Pool';
      toast.error(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (poolId: string) => vmPoolApi.deletePool(poolId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vm-pools'], refetchType: 'active' });
      toast.success('VM Pool deletion initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to delete VM Pool';
      toast.error(errorMessage);
    },
  });

  return {
    pools,
    totalCount: data?.total || 0,
    page: data?.page || page,
    size: data?.size || size,
    pages: data?.pages || 1,
    isLoading,
    isFetching,
    isError,
    refetch,
    createPool: createMutation.mutate,
    isCreating: createMutation.isPending,
    updatePool: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    scalePool: scaleMutation.mutate,
    isScaling: scaleMutation.isPending,
    deletePool: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export const useVMPool = (poolId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: pool, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['vm-pool', poolId],
    queryFn: () => vmPoolApi.getPool(poolId!),
    enabled: !!poolId,
    staleTime: 0,
    refetchInterval: 10000, // Poll every 10 seconds
  });

  return {
    pool: pool as VMPool | undefined,
    isLoading: isLoading || isPending,
    isError,
    refetch,
  };
};
